import React, { FC, useState } from 'react';
import { MenuItemInterface, MenuSubItemInterface } from '../../types';
import MenuSubItem from '../MenuSubItem';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '@hooks/useStore';
import {
  ChevronDownIcon,
  Spinner,
  Text
} from 'stubben-edge-unified-frontend-react-components';
import { Disclosure } from '@headlessui/react';
import {
  navLinkToTestSegment,
  navTitleToTestSegment
} from '../../navTestId';
import useFavourites from '@/hooks/useFavourites';
import FavouriteToggle from '@/components/common/FavouriteToggle';
import { createFavouriteItem } from '@/utils/favouritesStorage';
import { useSidebarCollapsed } from '../../../Sidebar/SidebarCollapsedContext';
import SidebarTooltip from '../../../Sidebar/SidebarTooltip';
import SidebarFlyout from '../../../Sidebar/SidebarFlyout';
import { canShowNavItem } from '../../canShowNavItem';

const isFavouritableNavTitle = (title: MenuItemInterface['title']) =>
  title !== 'Log out';

const collapsedButtonClass = (isActive: boolean) =>
  `flex h-12 w-full cursor-pointer items-center justify-center hover:border-r-2 hover:border-r-primary hover:bg-gray-50 focus:border-r-primary ${
    isActive ? 'border-r-2 border-r-primary bg-gray-50' : ''
  }`;

const renderNavIcon = (
  icon: MenuItemInterface['icon'],
  color: string
) =>
  icon && React.isValidElement(icon)
    ? React.cloneElement(icon as React.ReactElement, {
        size: 18,
        color
      })
    : icon;

const MenuItem: FC<MenuItemInterface> = ({
  title,
  icon,
  subItems,
  link,
  isExternal,
  onClick,
  isLoading
}) => {
  const { collapsed } = useSidebarCollapsed();
  const { authStore, interfaceStore } = useStore();
  const { hasPermission, hasFeature, user } = authStore;
  const cedarSubItem = subItems?.find((sub) => sub.title === 'Cedar');
  const navigate = useNavigate();
  const { isFavourite, toggleFavourite } = useFavourites();
  const [flyoutOpen, setFlyoutOpen] = useState(false);

  const checkCedar = !!(
    (cedarSubItem && !user?.hasCedarPermission) ||
    user?.hasCedarPermission === undefined
  );

  const visibleSubItems = checkCedar
    ? subItems?.filter((sub) => sub.title !== 'Cedar')
    : subItems;

  const processedSubItems = visibleSubItems?.map((sub) => {
    if (
      sub.title === 'Cedar' &&
      user?.hasCedarPermission &&
      user?.uuid &&
      sub.link
    ) {
      return {
        ...sub,
        link: `${sub.link}/${user.uuid}`,
        isExternal: true
      };
    }
    return sub;
  });

  const hasActiveSubItem =
    processedSubItems?.some((sub) =>
      sub.link
        ? window.location.pathname + window.location.search === sub.link
        : false
    ) ?? false;

  const isActive =
    window.location.pathname + window.location.search === link ||
    hasActiveSubItem;

  const canFavourite =
    !!link && !isExternal && isFavouritableNavTitle(title) && typeof title === 'string';
  const favouriteId = canFavourite ? `nav:${link}` : null;
  const favourited = favouriteId ? isFavourite(favouriteId) : false;
  const groupSegment = navTitleToTestSegment(title);
  const iconColor = isActive || flyoutOpen ? '#106df6' : '#667085';
  const ariaLabel = typeof title === 'string' ? title : undefined;

  const subItemList = (
    isLoading ? (
      <div
        className="flex items-center px-4 py-2"
        data-testid={`sidebar-nav-group-loading-${groupSegment}`}
      >
        <Spinner width={24} height={24} />
      </div>
    ) : (
      processedSubItems?.map(
        (subMenuItem: MenuSubItemInterface, index) =>
          canShowNavItem(subMenuItem, hasPermission, hasFeature) && (
            <MenuSubItem
              {...subMenuItem}
              parentTitle={title}
              key={index}
            />
          )
      )
    )
  );

  if (collapsed && processedSubItems) {
    return (
      <SidebarTooltip
        label={title}
        disabled={flyoutOpen}
        testIdentifier={`sidebar-nav-group-tooltip-${groupSegment}`}
      >
        <SidebarFlyout
          buttonContent={renderNavIcon(icon, iconColor)}
          buttonClassName={collapsedButtonClass(isActive || flyoutOpen)}
          buttonTestId={`sidebar-nav-group-${groupSegment}`}
          buttonAriaLabel={ariaLabel}
          panelTestId={`sidebar-nav-group-flyout-${groupSegment}`}
          onOpenChange={setFlyoutOpen}
        >
          <div
            className="flex flex-col gap-1"
            data-testid={`sidebar-nav-group-panel-${groupSegment}`}
          >
            {subItemList}
          </div>
        </SidebarFlyout>
      </SidebarTooltip>
    );
  }

  if (collapsed) {
    return (
      <SidebarTooltip
        label={title}
        testIdentifier={
          link
            ? `sidebar-nav-tooltip-${navLinkToTestSegment(link as string)}`
            : `sidebar-nav-tooltip-${groupSegment}`
        }
      >
        <button
          type="button"
          aria-label={ariaLabel}
          data-testid={
            link
              ? `sidebar-nav-${navLinkToTestSegment(link as string)}`
              : `sidebar-nav-action-${groupSegment}`
          }
          onClick={() => {
            onClick?.();
            if (isExternal) {
              window.open(link as string, '_blank');
            } else {
              navigate(link as string);
              interfaceStore.toggleMenuDrawer(false);
            }
          }}
          className={collapsedButtonClass(isActive)}
        >
          {renderNavIcon(icon, iconColor)}
        </button>
      </SidebarTooltip>
    );
  }

  return (
    <>
      {processedSubItems ? (
        <Disclosure
          // Re-mount when active sub item changes so defaultOpen is reapplied
          key={hasActiveSubItem ? 'open' : 'closed'}
          defaultOpen={hasActiveSubItem}
        >
          {({ open }) => (
            <>
              <Disclosure.Button
                data-testid={`sidebar-nav-group-${groupSegment}`}
                className={`flex cursor-pointer justify-between p-6 hover:border-r-2 hover:border-r-primary hover:bg-gray-50 focus:border-r-primary ${
                  open ? 'border-r-2 border-r-primary bg-gray-50' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  {renderNavIcon(icon, open ? '#106df6' : '#667085')}
                  <Text
                    size="sm"
                    variant={open ? 'primary' : 'quaternary'}
                    mode={open ? 'brand' : 'normal'}
                    bold={open ? 'medium' : 'regular'}
                    testIdentifier={`sidebar-nav-group-label-${groupSegment}`}
                  >
                    {title}
                  </Text>
                </div>
                <ChevronDownIcon
                  size={18}
                  color={open ? '#106df6' : '#667085'}
                  className={`transition-transform duration-200 ease-in-out ${
                    open ? 'rotate-180' : ''
                  }`}
                />
              </Disclosure.Button>
              <div
                className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
                  open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                }`}
                aria-hidden={!open}
              >
                <div className="min-h-0 overflow-hidden">
                  <div
                    className="relative mx-8 my-4 flex flex-col gap-2 pl-0.5"
                    data-testid={`sidebar-nav-group-panel-${groupSegment}`}
                  >
                    <span
                      aria-hidden
                      className={`pointer-events-none absolute bottom-3 left-0 top-0 w-0.5 ease-in-out transition-[opacity,background-color] [transition-duration:700ms,1400ms] ${
                        open
                          ? 'bg-gray-200 opacity-100'
                          : 'bg-primary opacity-0'
                      }`}
                    />
                    {subItemList}
                  </div>
                </div>
              </div>
            </>
          )}
        </Disclosure>
      ) : (
        (() => {
          const itemContent = (
            <>
              <div className="flex min-w-0 items-center gap-3">
                {renderNavIcon(icon, isActive ? '#106df6' : '#667085')}
                <Text
                  size="sm"
                  variant={isActive ? 'primary' : 'quaternary'}
                  mode={isActive ? 'brand' : 'normal'}
                  bold={isActive ? 'medium' : 'regular'}
                  testIdentifier={
                    link
                      ? `sidebar-nav-label-${navLinkToTestSegment(link as string)}`
                      : `sidebar-nav-label-${groupSegment}`
                  }
                >
                  {title}
                </Text>
              </div>
              {isLoading ? <Spinner width={24} height={24} /> : null}
            </>
          );

          const favouriteToggle =
            canFavourite && favouriteId ? (
              <div className="pr-4">
                <FavouriteToggle
                  isFavourited={favourited}
                  hoverReveal={!favourited}
                  testIdentifier={`sidebar-nav-favourite-${navLinkToTestSegment(
                    link as string
                  )}`}
                  onToggle={() =>
                    toggleFavourite(
                      createFavouriteItem({
                        type: 'nav',
                        stableKey: link as string,
                        title: title as string,
                        path: link as string
                      })
                    )
                  }
                />
              </div>
            ) : null;

          const wrapperClassName = `group flex items-center hover:border-r-2 hover:border-r-primary hover:bg-gray-50 focus-within:border-r-primary ${
            isActive ? 'border-r-2 border-r-primary bg-gray-50' : ''
          }`;
          const innerClassName =
            'flex min-w-0 flex-1 cursor-pointer justify-between p-6 text-left';

          if (link && isExternal) {
            return (
              <div className={wrapperClassName}>
                <a
                  href={link as string}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`sidebar-nav-${navLinkToTestSegment(link as string)}`}
                  onClick={() => onClick?.()}
                  className={innerClassName}
                >
                  {itemContent}
                </a>
                {favouriteToggle}
              </div>
            );
          }

          if (link) {
            return (
              <div className={wrapperClassName}>
                <Link
                  to={link as string}
                  data-testid={`sidebar-nav-${navLinkToTestSegment(link as string)}`}
                  onClick={() => {
                    onClick?.();
                    interfaceStore.toggleMenuDrawer(false);
                  }}
                  className={innerClassName}
                >
                  {itemContent}
                </Link>
                {favouriteToggle}
              </div>
            );
          }

          return (
            <div className={wrapperClassName}>
              <button
                type="button"
                data-testid={`sidebar-nav-action-${groupSegment}`}
                onClick={() => onClick?.()}
                className={innerClassName}
              >
                {itemContent}
              </button>
            </div>
          );
        })()
      )}
    </>
  );
};
export default MenuItem;
